import { tryCallApi } from 'common/helpers'
import { AssetLedger, FORM_MODES } from 'common/models'
import { FixedAssetsService, PresenterDepreciatedMonthsResponse } from 'common/open-api'
import { getAxiosSilent } from 'common/open-api/helper'
import { FeatureFlagNames } from 'common/open-features'
import { useEnableFlagRef } from 'common/open-features/hooks'
import { getIn, useFormikContext } from 'formik'
import { useCallback, useEffect, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { FORM_FIELDS, LEDGERS_FIELD_NAME } from '../const'
import { getAllLedgerFieldNames } from '../helper'
import { matchDateConditions, matchDepreciationConditions } from '../helpers/should-render-depreciation-month'
import { useWaitFormReady } from './use-wait-form-ready'

export const filterLedger = (ledger: Partial<AssetLedger>) =>
  !!ledger.ledger_setting_id && matchDepreciationConditions(Number(ledger.depreciation_method_code))

export const shouldCalculateDepreciationMonth = (
  acquisitionCost?: number,
  transitionAt?: string,
  usageStartedAt?: string,
  ledgers?: Partial<AssetLedger>[]
) => {
  const ledgerDataAvailable = ledgers?.some(filterLedger) || false
  return ledgerDataAvailable && !!acquisitionCost && matchDateConditions(usageStartedAt, transitionAt)
}

export const useCalculateDepreciationMonthNew = () => {
  const { setFieldValue } = useFormikContext()
  return useCallback(
    async (
      acquisitionCost?: number,
      subsidyAmount?: number,
      transitionAt?: string,
      usageStartedAt?: string,
      ledgers?: (Partial<AssetLedger> & { ledgerIndex?: number })[]
    ) => {
      const shouldCalculate = shouldCalculateDepreciationMonth(acquisitionCost, transitionAt, usageStartedAt, ledgers)

      if (shouldCalculate) {
        const { data } = await tryCallApi(FixedAssetsService.postFixedAssetsCalculateDepreciatedMonths)(
          {
            acquisition_cost: acquisitionCost ?? 0,
            subsidy_amount: subsidyAmount ?? 0,
            transition_at: transitionAt ?? '',
            usage_started_at: usageStartedAt ?? '',
            ledgers: (ledgers ?? [])
              .map((ledger, index) => ({
                ...ledger,
                ledgerIndex: ledger.ledgerIndex ?? index,
              }))
              .filter(filterLedger)
              .map(ledger => ({
                depreciation_method: Number(ledger.depreciation_method_code),
                id: ledger.ledgerIndex + 1,
                ledger_setting_id: Number(ledger.ledger_setting_id),
                service_life: Number(ledger.service_life),
                usage_period_months: Number(ledger.usage_period_months),
                memorandum_value: ledger.memorandum_value ?? undefined,
                transition_book_value: ledger.transition_book_value ?? undefined,
              })),
          },
          getAxiosSilent()
        )
        ;(data?.data as PresenterDepreciatedMonthsResponse | undefined)?.ledgers?.forEach(ledger => {
          const { depreciatedMonthsField } = getAllLedgerFieldNames(ledger.id - 1)
          setFieldValue(depreciatedMonthsField, ledger.depreciated_months ?? 0)
        })
      }
    },
    []
  )
}

export const useCalculateDepreciationMonthAllLedgers = (
  disabled: boolean,
  formMode: FORM_MODES,
  acquisitionCost?: number
) => {
  const { values, getFieldProps, setFieldValue } = useFormikContext()
  const transitionAt = getIn(values, 'transition_at')
  const usageStartedAt = getIn(values, 'usage_started_at')
  const subsidyAmount = getIn(values, 'subsidy_amount')

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.DepreciationMonth, flagEnableRef)

  const calculateDepreciationMonth = useCalculateDepreciationMonthNew()

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const ledgers = getFieldProps(LEDGERS_FIELD_NAME).value as AssetLedger[]
    const shouldCalculate = shouldCalculateDepreciationMonth(acquisitionCost, transitionAt, usageStartedAt, ledgers)
    if (shouldCalculate) {
      calculateDepreciationMonth(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
    } else {
      ledgers.forEach((_, index) => {
        const depreciationMonthField = getAllLedgerFieldNames(index).depreciatedMonthsField
        setFieldValue(depreciationMonthField, undefined)
      })
    }
  }, [acquisitionCost, transitionAt, usageStartedAt, subsidyAmount, disabled, calculateDepreciationMonth])
}

export const useCalculateDepreciationMonthLedger = (disabled: boolean, formMode: FORM_MODES, ledgerIndex: number) => {
  const { values, getFieldProps, setFieldValue } = useFormikContext()
  const allLedgerFields = getAllLedgerFieldNames(ledgerIndex)
  const depreciationMethodCode = getIn(values, allLedgerFields.depreciationMethodCodeField)
  const ledgerSettingId = getIn(values, allLedgerFields.ledgerSettingIdField)
  const memorandumValue = getIn(values, allLedgerFields.memorandumValueField)
  const transitionBookValue = getIn(values, allLedgerFields.transitionBookValueField)
  const serviceLife = getIn(values, allLedgerFields.serviceLifeField)
  const usagePeriodMonths = getIn(values, allLedgerFields.usagePeriodMonthsField)

  const formReadyRef = useRef(false)
  useWaitFormReady(formMode, formReadyRef)

  const flagEnableRef = useRef(false)
  useEnableFlagRef(FeatureFlagNames.DepreciationMonth, flagEnableRef)

  // Need to use debounce here, because when changing d method, other fields are changed too
  const calculateDepreciationMonth = useDebouncedCallback(useCalculateDepreciationMonthNew(), 300)

  useEffect(() => {
    if (!formReadyRef.current || !flagEnableRef.current || disabled) {
      return
    }
    const acquisitionCost = getFieldProps(FORM_FIELDS.ACQUISITION_COST_REFERENCE).value
    const transitionAt = getFieldProps('transition_at').value
    const usageStartedAt = getFieldProps('usage_started_at').value
    const subsidyAmount = getFieldProps('subsidy_amount').value

    const ledgers = [
      {
        ledgerIndex,
        depreciation_method_code: depreciationMethodCode,
        ledger_setting_id: ledgerSettingId,
        memorandum_value: memorandumValue,
        transition_book_value: transitionBookValue,
        service_life: serviceLife,
        usage_period_months: usagePeriodMonths,
      },
    ]

    const shouldCalculate = shouldCalculateDepreciationMonth(acquisitionCost, transitionAt, usageStartedAt, ledgers)
    if (shouldCalculate) {
      calculateDepreciationMonth(acquisitionCost, subsidyAmount, transitionAt, usageStartedAt, ledgers)
    } else {
      setFieldValue(allLedgerFields.depreciatedMonthsField, undefined)
    }
  }, [
    depreciationMethodCode,
    ledgerSettingId,
    memorandumValue,
    transitionBookValue,
    serviceLife,
    usagePeriodMonths,
    ledgerIndex,
    disabled,
    calculateDepreciationMonth,
  ])
}
